import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Download, FileDown, Trash2, Edit, Eye, Share2, FileText, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { toast } from 'sonner';
import DeleteModal from './DeleteModal';
import SeguimientoModal from './SeguimientoModal';
import './Detail.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Detail = ({ posts, fetchPosts, onEdit }) => {
  const [filters, setFilters] = useState({
    q: '',
    category: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSeguimientoModal, setShowSeguimientoModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [templates, setTemplates] = useState({});

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [posts, sortConfig]);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/text-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const templateMap = {};
      (response.data || []).forEach(t => {
        templateMap[t.key] = t.content;
      });
      setTemplates(templateMap);
    } catch (error) {
      console.error('Error al cargar plantillas:', error);
    }
  };

  const applyFilters = () => {
    let result = [...posts];

    // Filtro de búsqueda de texto
    if (filters.q) {
      const q = filters.q.toLowerCase();
      result = result.filter(p => 
        (p.author || '').toLowerCase().includes(q) ||
        (p.mentioned || '').toLowerCase().includes(q) ||
        (p.desc || '').toLowerCase().includes(q)
      );
    }

    // Filtro de categoría
    if (filters.category) {
      result = result.filter(p => p.category === filters.category);
    }

    // Filtro de estado
    if (filters.status) {
      result = result.filter(p => p.Estado_Actual === filters.status);
    }

    // Filtro por rango de fechas
    if (filters.dateFrom) {
      const dateFrom = new Date(filters.dateFrom);
      result = result.filter(p => {
        const postDate = new Date(p.date);
        return postDate >= dateFrom;
      });
    }

    if (filters.dateTo) {
      const dateTo = new Date(filters.dateTo);
      dateTo.setHours(23, 59, 59, 999); // Incluir todo el día
      result = result.filter(p => {
        const postDate = new Date(p.date);
        return postDate <= dateTo;
      });
    }

    // Aplicar ordenamiento
    result = applySorting(result);
    setFilteredPosts(result);
  };

  const applySorting = (data) => {
    if (!sortConfig.key) return data;

    const sorted = [...data].sort((a, b) => {
      let aVal, bVal;

      switch (sortConfig.key) {
        case 'date':
          aVal = new Date(a.date || 0);
          bVal = new Date(b.date || 0);
          break;
        case 'author':
          aVal = (a.author || '').toLowerCase();
          bVal = (b.author || '').toLowerCase();
          break;
        case 'institution':
          aVal = (a.mentioned || '').toLowerCase();
          bVal = (b.mentioned || '').toLowerCase();
          break;
        case 'category':
          aVal = (a.category || '').toLowerCase();
          bVal = (b.category || '').toLowerCase();
          break;
        case 'status':
          aVal = (a.Estado_Actual || '').toLowerCase();
          bVal = (b.Estado_Actual || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown size={14} className="sort-icon inactive" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={14} className="sort-icon active" />
      : <ArrowDown size={14} className="sort-icon active" />;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Registrado':
        return 'status-badge status-registrado';
      case 'Enviado/Compartido':
        return 'status-badge status-enviado';
      case 'En proceso averiguación':
        return 'status-badge status-proceso';
      case 'Archivado':
        return 'status-badge status-archivado';
      default:
        return 'status-badge';
    }
  };

  const handleDelete = (post) => {
    setSelectedPost(post);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (pin) => {
    try {
      await axios.delete(`${API}/posts/${selectedPost.id}`, {
        data: { pin }
      });
      toast.success('Publicación eliminada correctamente');
      setShowDeleteModal(false);
      setSelectedPost(null);
      fetchPosts();
    } catch (error) {
      console.error('Error al eliminar:', error);
      if (error.response?.status === 403) {
        toast.error('PIN incorrecto');
      } else {
        toast.error('Error al eliminar la publicación');
      }
    }
  };

  const handleSeguimiento = (post) => {
    setSelectedPost(post);
    setShowSeguimientoModal(true);
  };

  const handleSeguimientoSave = async (data) => {
    try {
      await axios.post(`${API}/posts/${selectedPost.id}/seguimiento`, data);
      toast.success('Seguimiento registrado correctamente');
      setShowSeguimientoModal(false);
      setSelectedPost(null);
      fetchPosts();
    } catch (error) {
      console.error('Error al registrar seguimiento:', error);
      toast.error('Error al registrar seguimiento');
    }
  };

  const handleShare = async (post) => {
    const shareTitle = templates.share_title || 'Importancia ALTA — Publicación sobre posible acto de corrupción';
    const shareHeader = templates.share_header || '— Detalle del registro —';
    const shareNativeTitle = templates.share_native_title || 'Monitoreo — Publicación relevante';
    const shareIntro = templates.share_intro || 'Estimado/a colaborador,\n\nPor este medio, les comparto la publicación identificada recientemente que podría estar relacionada con posibles irregularidades en la gestión pública.\n\nEs importante revisar y analizar el contenido para determinar si contiene elementos que requieran acciones preventivas o de verificación, con el fin de fortalecer los mecanismos institucionales de transparencia y rendición de cuentas.\n\nAgradezco su pronta atención y colaboración para efectuar el análisis correspondiente.';
    const shareClosing = templates.share_closing || 'Gracias por su colaboración.\nAtentamente,';

    const text = `${shareIntro}

${shareTitle}

${shareHeader}
Fecha: ${(post.date || '').slice(0, 10)}
Autor: ${post.author || 'N/A'}
Red Social: ${post.network || 'N/A'}
Institución: ${post.mentioned || 'N/A'}
Personas mencionadas: ${post.persons || 'N/A'}
Categoría: ${post.category || 'N/A'}
Descripción: ${post.desc || 'N/A'}
Enlace: ${post.link || 'N/A'}

${shareClosing}`;

    try {
      // Cambiar estado a "Enviado/Compartido" si está en "Registrado"
      if (post.Estado_Actual === 'Registrado') {
        const now = new Date().toISOString();
        await axios.post(`${API}/posts/${post.id}/seguimiento`, {
          status: 'Enviado/Compartido',
          comment: 'Post compartido automáticamente',
          date: now
        });
        
        toast.success('Estado actualizado a Enviado/Compartido');
        await fetchPosts();
      }
      
      // Generar PDF
      const pdf = generatePostPDF(post);
      const pdfBlob = pdf.output('blob');
      const pdfFile = new File([pdfBlob], `publicacion_${post.id}.pdf`, { type: 'application/pdf' });
      
      // Intentar compartir con archivo si es posible
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        try {
          await navigator.share({
            title: shareNativeTitle,
            text,
            files: [pdfFile]
          });
          toast.success('Compartido correctamente');
          return;
        } catch (shareError) {
          console.log('Error al compartir con archivo, intentando sin archivo:', shareError);
        }
      }
      
      // Si no se puede compartir con archivo, compartir solo texto
      if (navigator.share) {
        await navigator.share({
          title: shareNativeTitle,
          text,
          url: post.link || undefined
        });
        toast.info('Texto compartido. El PDF no pudo ser adjuntado automáticamente.');
      } else {
        // Copiar al portapapeles y descargar PDF
        await navigator.clipboard.writeText(text);
        pdf.save(`publicacion_${post.id}.pdf`);
        toast.success('Texto copiado al portapapeles y PDF descargado');
      }
    } catch (error) {
      try {
        await navigator.clipboard.writeText(text);
        toast.success('Texto copiado al portapapeles');
      } catch (err) {
        toast.error('No se pudo compartir');
      }
    }
  };

  const generatePostPDF = (post) => {
    try {
      const pdfTitle = templates.pdf_title || 'Monitoreo de Medios';
      const pdfSubtitle = templates.pdf_subtitle || 'Reporte de Publicación por Posible Acto de Corrupción a Investigar';

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 20;
      
      // Header con fondo azul
      pdf.setFillColor(30, 136, 229);
      pdf.rect(0, 0, pageWidth, 35, 'F');
      
      // Título
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.text(pdfTitle, pageWidth / 2, 15, { align: 'center' });
      
      pdf.setFontSize(12);
      pdf.text(pdfSubtitle, pageWidth / 2, 25, { align: 'center' });
      
      let y = 45;
      
      // Fecha de generación
      pdf.setTextColor(100, 100, 100);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const dateStr = new Date().toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      pdf.text(`Generado: ${dateStr}`, margin, y);
      y += 8;

      // Línea divisoria
      pdf.setDrawColor(30, 136, 229);
      pdf.setLineWidth(0.5);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 12;

      // Función para agregar filas
      const addRow = (label, value) => {
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 136, 229);
        pdf.setFontSize(11);
        pdf.text(label + ':', margin, y);
        
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(80, 80, 80);
        pdf.setFontSize(10);
        const lines = pdf.splitTextToSize((value || 'N/A').toString(), pageWidth - margin - 65);
        pdf.text(lines, margin + 60, y);
        y += lines.length * 6 + 5;
        
        if (y > 260) {
          pdf.addPage();
          y = 20;
        }
      };

      addRow('ID', post.id);
      addRow('Fecha del post', (post.date || '').slice(0, 10));
      addRow('Autor', post.author);
      addRow('Red social', post.network);
      addRow('Institución', post.mentioned);
      addRow('Personas mencionadas', post.persons || 'N/A');
      addRow('Categoría', post.category);
      addRow('Estado actual', post.Estado_Actual);

      y += 5;
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 136, 229);
      pdf.setFontSize(11);
      pdf.text('Descripción:', margin, y);
      y += 7;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.setFontSize(10);
      const descLines = pdf.splitTextToSize(post.desc || 'N/A', pageWidth - (margin * 2));
      pdf.text(descLines, margin, y);
      y += descLines.length * 6 + 10;

      if (post.link) {
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 136, 229);
        pdf.text('Enlace:', margin, y);
        y += 6;
        pdf.setTextColor(28, 92, 190);
        pdf.setFont('helvetica', 'normal');
        pdf.textWithLink('Clic aquí para ver la publicación', margin, y, { url: post.link });
        pdf.setTextColor(0, 0, 0);
        y += 10;
      }

      // Nota final
      y += 8;
      pdf.setDrawColor(220, 220, 220);
      pdf.line(margin, y, pageWidth - margin, y);
      y += 8;
      
      pdf.setFont('helvetica', 'italic');
      pdf.setTextColor(120, 120, 120);
      pdf.setFontSize(9);
      const pdfNote = templates.pdf_note || 'Este reporte ha sido generado para su análisis y seguimiento institucional. La publicación podría contener elementos relevantes para fortalecer la transparencia, la rendición de cuentas y la prevención de la corrupción en la gestión pública.';
      const noteLines = pdf.splitTextToSize(pdfNote, pageWidth - (margin * 2));
      pdf.text(noteLines, margin, y);

      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      const pdfFooter = templates.pdf_footer || 'ALERTA 360 — Sistema Integral de Gestión de Casos — 2025';
      pdf.text(
        pdfFooter,
        pageWidth / 2,
        pdf.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );

      return pdf;
    } catch (error) {
      console.error('Error al generar PDF:', error);
      toast.error('Error al generar PDF');
      return null;
    }
  };

  const handleExportPostPDF = (post) => {
    try {
      const pdf = generatePostPDF(post);
      if (pdf) {
        pdf.save(`reporte_${post.id}.pdf`);
        toast.success('PDF generado correctamente');
      }
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      toast.error('Error al exportar PDF');
    }
  };

  const exportToExcel = () => {
    try {
      if (!filteredPosts || filteredPosts.length === 0) {
        toast.warning('No hay datos para exportar');
        return;
      }

      const data = filteredPosts.map(p => ({
        ID: p.id || '',
        Fecha: (p.date || '').slice(0, 10),
        Autor: p.author || '',
        Red: p.network || '',
        Institución: p.mentioned || '',
        Personas: p.persons || '',
        Categoría: p.category || '',
        Estado: p.Estado_Actual || '',
        Descripción: p.desc || '',
        Enlace: p.link || ''
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      
      // Ajustar ancho de columnas
      const colWidths = [
        { wch: 15 }, // ID
        { wch: 12 }, // Fecha
        { wch: 20 }, // Autor
        { wch: 12 }, // Red
        { wch: 35 }, // Institución
        { wch: 20 }, // Personas
        { wch: 15 }, // Categoría
        { wch: 20 }, // Estado
        { wch: 50 }, // Descripción
        { wch: 40 }  // Enlace
      ];
      ws['!cols'] = colWidths;
      
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Detalle');
      XLSX.writeFile(wb, `monitoreo_medios_detalle_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Excel exportado correctamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      toast.error('Error al exportar Excel: ' + error.message);
    }
  };

  const exportDetailToPDF = () => {
    try {
      if (!filteredPosts || filteredPosts.length === 0) {
        toast.warning('No hay datos para exportar');
        return;
      }

      const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape para mejor visualización
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      
      // Header con fondo azul
      pdf.setFillColor(30, 136, 229);
      pdf.rect(0, 0, pageWidth, 35, 'F');
      
      // Título
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(22);
      pdf.text('Monitoreo de Medios', pageWidth / 2, 15, { align: 'center' });
      
      pdf.setFontSize(14);
      pdf.text('Detalle de Publicaciones', pageWidth / 2, 25, { align: 'center' });
      
      // Información
      pdf.setTextColor(80, 80, 80);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      const dateStr = new Date().toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      pdf.text(`Fecha de generación: ${dateStr}`, margin, 42);
      pdf.text(`Total de registros: ${filteredPosts.length}`, pageWidth - margin - 60, 42);

      const tableData = filteredPosts.map(p => [
        (p.date || '').slice(0, 10),
        (p.author || '').slice(0, 20),
        p.network || '',
        (p.mentioned || '').slice(0, 30) + ((p.mentioned || '').length > 30 ? '...' : ''),
        (p.persons || 'N/A').slice(0, 20),
        p.category || '',
        (p.Estado_Actual || '').slice(0, 20),
        (p.desc || '').slice(0, 60) + ((p.desc || '').length > 60 ? '...' : '')
      ]);

      pdf.autoTable({
        startY: 48,
        head: [['Fecha', 'Autor', 'Red', 'Institución', 'Personas', 'Categoría', 'Estado', 'Descripción']],
        body: tableData,
        styles: { 
          font: 'helvetica', 
          fontSize: 7, 
          cellPadding: 2.5,
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
          overflow: 'linebreak',
          cellWidth: 'wrap'
        },
        headStyles: { 
          fillColor: [30, 136, 229],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'center'
        },
        alternateRowStyles: {
          fillColor: [245, 247, 251]
        },
        columnStyles: {
          0: { cellWidth: 22, halign: 'center' },  // Fecha
          1: { cellWidth: 30 },  // Autor
          2: { cellWidth: 18, halign: 'center' },  // Red
          3: { cellWidth: 45 },  // Institución
          4: { cellWidth: 25 },  // Personas
          5: { cellWidth: 25, halign: 'center' },  // Categoría
          6: { cellWidth: 30, halign: 'center' },  // Estado
          7: { cellWidth: 70 }   // Descripción
        },
        margin: { top: 10, left: margin, right: margin },
        didDrawPage: (data) => {
          // Footer en cada página
          pdf.setFontSize(8);
          pdf.setTextColor(150, 150, 150);
          const pageNum = pdf.internal.getCurrentPageInfo().pageNumber;
          pdf.text(
            `ALERTA 360 — Sistema Integral de Gestión de Casos — 2025 | Página ${pageNum}`,
            pageWidth / 2,
            pageHeight - 8,
            { align: 'center' }
          );
        }
      });

      pdf.save(`monitoreo_medios_detalle_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF generado correctamente');
    } catch (error) {
      console.error('Error al generar PDF del detalle:', error);
      toast.error('Error al generar PDF: ' + error.message);
    }
  };

  return (
    <div className="detail-container fade-in" data-testid="detail-view">
      <div className="container">
        {/* Header */}
        <div className="panel detail-header">
          <div>
            <h2 className="panel-title">Detalle de Publicaciones</h2>
            <p className="detail-count" data-testid="detail-count">
              {filteredPosts.length} registro{filteredPosts.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="detail-actions">
            <button className="btn btn-secondary btn-sm" onClick={fetchPosts} data-testid="refresh-detail-btn">
              <RefreshCw size={16} />
              Actualizar
            </button>
            <button className="btn btn-secondary btn-sm" onClick={exportToExcel} data-testid="export-detail-excel-btn">
              <FileDown size={16} />
              Excel
            </button>
            <button className="btn btn-primary btn-sm" onClick={exportDetailToPDF} data-testid="export-detail-pdf-btn">
              <Download size={16} />
              Exportar PDF
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="panel filters-compact">
          <div className="filters-row">
            <div className="filter-item">
              <label>Búsqueda</label>
              <input
                type="text"
                data-testid="detail-search"
                placeholder="Autor, institución, descripción..."
                value={filters.q}
                onChange={(e) => handleFilterChange('q', e.target.value)}
              />
            </div>
            <div className="filter-item">
              <label>Fecha Desde</label>
              <input
                type="date"
                data-testid="detail-date-from"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>
            <div className="filter-item">
              <label>Fecha Hasta</label>
              <input
                type="date"
                data-testid="detail-date-to"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
            <div className="filter-item">
              <label>Categoría</label>
              <select
                data-testid="detail-category"
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="">Todas</option>
                <option value="ATENDER">ATENDER</option>
                <option value="SEGUIMIENTO">SEGUIMIENTO</option>
                <option value="MONITOREO">MONITOREO</option>
              </select>
            </div>
            <div className="filter-item">
              <label>Estado</label>
              <select
                data-testid="detail-status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="Registrado">Registrado</option>
                <option value="Enviado/Compartido">Enviado/Compartido</option>
                <option value="En proceso averiguación">En proceso averiguación</option>
                <option value="Archivado">Archivado</option>
              </select>
            </div>
            <button className="btn btn-primary" onClick={applyFilters} data-testid="apply-detail-filters-btn">
              <Search size={16} />
              Buscar
            </button>
          </div>
        </div>

        {/* Tabla */}
        <div className="panel table-panel">
          <div className="table-wrapper">
            <table className="detail-table" data-testid="detail-table">
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('date')}>
                    <div className="th-content">
                      <span>Fecha</span>
                      {getSortIcon('date')}
                    </div>
                  </th>
                  <th className="sortable" onClick={() => handleSort('author')}>
                    <div className="th-content">
                      <span>Autor</span>
                      {getSortIcon('author')}
                    </div>
                  </th>
                  <th>Red</th>
                  <th className="sortable" onClick={() => handleSort('institution')}>
                    <div className="th-content">
                      <span>Institución</span>
                      {getSortIcon('institution')}
                    </div>
                  </th>
                  <th>Personas</th>
                  <th className="sortable" onClick={() => handleSort('category')}>
                    <div className="th-content">
                      <span>Categoría</span>
                      {getSortIcon('category')}
                    </div>
                  </th>
                  <th className="sortable" onClick={() => handleSort('status')}>
                    <div className="th-content">
                      <span>Estado</span>
                      {getSortIcon('status')}
                    </div>
                  </th>
                  <th>Descripción</th>
                  <th>Enlace</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredPosts.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="empty-state">
                      No se encontraron registros
                    </td>
                  </tr>
                ) : (
                  filteredPosts.map((post) => (
                    <tr key={post.id} data-testid={`post-row-${post.id}`}>
                      <td>{(post.date || '').slice(0, 10)}</td>
                      <td>{post.author}</td>
                      <td>{post.network}</td>
                      <td>{post.mentioned}</td>
                      <td className="persons-cell" title={post.persons}>
                        {post.persons || '-'}
                      </td>
                      <td><span className="category-badge">{post.category}</span></td>
                      <td>
                        <span className={getStatusBadgeClass(post.Estado_Actual)}>
                          {post.Estado_Actual}
                        </span>
                      </td>
                      <td className="desc-cell" title={post.desc}>
                        {(post.desc || '').length > 80 
                          ? post.desc.slice(0, 80) + '...' 
                          : post.desc}
                      </td>
                      <td>
                        {post.link && (
                          <a 
                            href={post.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="link-btn"
                            data-testid={`post-link-${post.id}`}
                          >
                            Abrir
                          </a>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn action-seguimiento"
                            onClick={() => handleSeguimiento(post)}
                            title="Seguimiento"
                            data-testid={`seguimiento-btn-${post.id}`}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            className="action-btn action-edit"
                            onClick={() => onEdit(post)}
                            title="Editar"
                            data-testid={`edit-btn-${post.id}`}
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            className="action-btn action-delete"
                            onClick={() => handleDelete(post)}
                            title="Eliminar"
                            data-testid={`delete-btn-${post.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                          <button
                            className="action-btn action-share"
                            onClick={() => handleShare(post)}
                            title="Compartir"
                            data-testid={`share-btn-${post.id}`}
                          >
                            <Share2 size={14} />
                          </button>
                          <button
                            className="action-btn action-pdf"
                            onClick={() => handleExportPostPDF(post)}
                            title="Generar PDF"
                            data-testid={`pdf-btn-${post.id}`}
                          >
                            <FileText size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modales */}
      {showDeleteModal && (
        <DeleteModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {showSeguimientoModal && (
        <SeguimientoModal
          post={selectedPost}
          onClose={() => setShowSeguimientoModal(false)}
          onSave={handleSeguimientoSave}
        />
      )}
    </div>
  );
};

export default Detail;
